import { record } from 'rrweb';
import { EventType, IncrementalSource, MouseInteractions } from '@rrweb/types';
import Agent from '.';
import Recorder from './recorder';
import Stasher from './stasher';
import canDiscover from '../common/canDiscover';

const USER_ACTIONS = [
  IncrementalSource.MouseMove,
  IncrementalSource.MouseInteraction,
  IncrementalSource.Scroll,
  IncrementalSource.ViewportResize,
  IncrementalSource.Input,
  IncrementalSource.TouchMove,
  IncrementalSource.MediaInteraction,
  IncrementalSource.Drag,
];

class RecordingManager {
  agent: Agent;
  recorder: Recorder;
  stasher: Stasher;

  constructor(agent: Agent) {
    this.agent = agent;
    this.recorder = new Recorder(this.handleEvent.bind(this));
    this.stasher = new Stasher(this);
  }

  start() {
    this.recorder.start();
  }

  handleEvent(event: any) {
    const url = new URL(window.location.href);
    const cleanUrl = url.origin + url.pathname;

    if (
      !canDiscover(
        this.agent.recordingBlocklist,
        this.agent._allowLocalhostRecording
      )
    ) {
      return;
    }

    if (this.agent.sessionManager.isIdle) {
      if (this.isInteractiveEvent(event)) {
        this.agent.handleActivityDuringIdle();
      } else {
        // if the session is idle, discard non-interactive events to avoid session refresh
        return;
      }
    }

    if (this.isClick(event)) {
      let clickedNode = record.mirror.getNode(event.data.id);
      if (clickedNode && this.nodeIsInteresting(clickedNode)) {
        event['conversionData'] = {};
        event.conversionData.eventType = 'click';
        event.conversionData.textContent = (
          clickedNode as unknown as HTMLElement
        ).textContent;
        event.conversionData.htmlElement = (
          clickedNode as unknown as HTMLElement
        ).nodeName?.toLowerCase();
        event.conversionData.location = cleanUrl;
      }
    }

    if (this.isCustom(event)) {
      event['conversionData'] = {};
      event.conversionData.eventType = 'custom';
      event.conversionData.customEventType = event.data.tag;
      event.conversionData.location = cleanUrl;
    }

    if (this.stasher.isRunning) {
      this.stasher.handle(event);
      return;
    }

    this.publishEvent(event);
  }

  publishEvent(event: any) {
    this.agent.sender.handle(event);
    // restart the timer
    if (this.agent.timer) {
      this.agent.timer.restart();
    }
  }

  startRecorderWithStasher() {
    this.stasher.start();
    this.recorder = new Recorder(this.handleEvent.bind(this));
    this.recorder.start();
  }

  private isClick(event: {
    type: number;
    data: { source: number; type: number };
  }) {
    return (
      event.type === EventType.IncrementalSnapshot && // incremental snapshot event
      event.data.source === IncrementalSource.MouseInteraction && // source of incremental snapshot is a mouse action
      event.data.type === MouseInteractions.Click // mouse action is a click
    );
  }

  private nodeIsInteresting(clickedNode: Node): boolean {
    return (
      clickedNode.nodeName === 'BUTTON' || clickedNode.nodeName === 'ANCHOR'
    );
  }

  private isCustom(event: { type: number }) {
    return event.type === EventType.Custom;
  }

  private isInteractiveEvent(event: any) {
    return (
      event.type === EventType.IncrementalSnapshot &&
      USER_ACTIONS.includes(event.data?.source)
    );
  }
}

export default RecordingManager;
